import { useEffect, useRef } from "react";
import { useArtifactsWithProgressiveSizes } from "../hooks/useArtifactsWithProgressiveSizes";
import type { Dataset, EnrichedArtifact } from "../ArtifactSelectorModal";

interface DatasetArtifactStreamProps {
  dataset: Dataset;
  searchQuery: string;
  enabled: boolean;
  onArtifactsUpdate: (datasetId: number, artifacts: EnrichedArtifact[]) => void;
}

/**
 * Check if artifacts have actually changed by comparing their IDs and key properties
 * Avoids infinite loops caused by array reference changes
 * But detects when artifact properties (like filesize) change
 */
const artifactsHaveChanged = (
  prev: EnrichedArtifact[] | undefined,
  next: EnrichedArtifact[]
): boolean => {
  if (!prev) return true;
  if (prev.length !== next.length) return true;

  // Create maps for efficient lookup
  const prevMap = new Map(prev.map((a) => [a.id, a]));
  const nextMap = new Map(next.map((a) => [a.id, a]));

  // Check if any IDs changed
  if (prevMap.size !== nextMap.size) return true;

  // Check if any artifact object changed (including filesize)
  for (const [id, nextArtifact] of nextMap) {
    const prevArtifact = prevMap.get(id);
    if (!prevArtifact) return true; // New artifact

    // Check if filesize changed (the main thing we care about)
    if (prevArtifact.filesize !== nextArtifact.filesize) return true;

    // For other fields, just do a shallow comparison
    if (
      prevArtifact.filename !== nextArtifact.filename ||
      prevArtifact.filetype !== nextArtifact.filetype
    ) {
      return true;
    }
  }

  return false;
};

/**
 * Component that manages SSE streaming for a single dataset's artifacts
 * Uses the progressive size loading hook and notifies parent of updates
 *
 * This component doesn't render anything - it purely manages the stream state
 * and communicates updates to the parent component via callback
 */
export const DatasetArtifactStream = ({
  dataset,
  searchQuery,
  enabled,
  onArtifactsUpdate,
}: DatasetArtifactStreamProps) => {
  // Each instance of this component gets its own hook call
  // This satisfies React's Rules of Hooks since the component hierarchy is stable
  const stream = useArtifactsWithProgressiveSizes({
    datasetId: dataset.id,
    datasetName: dataset.name,
    searchQuery,
    enabled,
  });

  // Track previous artifacts to avoid infinite loops
  const prevArtifactsRef = useRef<EnrichedArtifact[]>();

  // Notify parent when artifacts actually change (by content, not reference)
  useEffect(() => {
    if (artifactsHaveChanged(prevArtifactsRef.current, stream.artifacts)) {
      prevArtifactsRef.current = stream.artifacts;
      onArtifactsUpdate(dataset.id, stream.artifacts);
    }
  }, [stream.artifacts, dataset.id, onArtifactsUpdate]);

  // This component doesn't render anything
  return null;
};
